import type { TableStateControl, UseTableStoreStateOptions } from '@civilgrid/ui/hooks';
import { createEmptyFilterGroup, enrichFilterGroupWithFilterTypes } from '@civilgrid/ui/hooks';
import { DEFAULT_PAGE_SIZE, type FilterGroupItem, type FilterType } from '@civilgrid/ui/utils';
import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table';
import { useEffect, useMemo, useRef } from 'react';
import { useSnapshot } from 'valtio';
import {
  homeViewStore,
  resetFilters,
  setCurrentPage,
  setFilters,
  setGlobalFilter,
  setPageSize,
  setPagination,
  setSorting,
} from '../store';

export type UseHomeViewTableStateOptions<TData = unknown> = UseTableStoreStateOptions<TData> & {
  /** When provided, returned filters are enriched with filterValueConfig for display (dates, select labels). */
  filterTypes?: FilterType[];
};

/**
 * Table state control backed by homeViewV2 Valtio store. Uses the same options
 * and return shape as useTableStoreState. Shared between ProjectsTable and
 * ProjectsMapList so filters/sort/pagination stay in sync.
 */
export function useHomeViewTableState<TData = unknown>(
  options: UseHomeViewTableStateOptions<TData> = {},
): TableStateControl {
  const {
    initialFilters,
    initialGlobalFilter = '',
    initialSorting = [],
    initialPagination,
    defaultPageSize = DEFAULT_PAGE_SIZE,
    filterTypes,
  } = options;

  const snap = useSnapshot(homeViewStore);
  const hasInitialized = useRef(false);

  // Apply initial state once (same contract as useTableStoreState)
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    if (initialFilters != null) homeViewStore.filters = initialFilters;
    if (initialGlobalFilter !== '') homeViewStore.globalFilter = initialGlobalFilter;
    if (initialSorting.length > 0) homeViewStore.sorting = initialSorting as SortingState;
    if (initialPagination != null) {
      homeViewStore.pagination = initialPagination as PaginationState;
    } else if (defaultPageSize !== DEFAULT_PAGE_SIZE) {
      homeViewStore.pagination = {
        pageIndex: 0,
        pageSize: defaultPageSize,
      };
    }
  }, [initialFilters, initialGlobalFilter, initialSorting, initialPagination, defaultPageSize]);

  const handlePaginationChange: OnChangeFn<PaginationState> = (updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(homeViewStore.pagination) : updaterOrValue;
    setPagination(next);
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(homeViewStore.sorting) : updaterOrValue;
    setSorting(next);
  };

  const currentPage = snap.pagination.pageIndex + 1;

  const resetToDefaults = () => {
    homeViewStore.filters = initialFilters ?? createEmptyFilterGroup();
    homeViewStore.globalFilter = '';
    homeViewStore.sorting = initialSorting as SortingState;
    homeViewStore.pagination = { pageIndex: 0, pageSize: defaultPageSize };
  };

  const filtersForDisplay = useMemo(() => {
    const raw = snap.filters as FilterGroupItem;
    if (!filterTypes?.length) return raw;
    return enrichFilterGroupWithFilterTypes(raw, filterTypes);
  }, [snap.filters, filterTypes]);

  return {
    filters: filtersForDisplay,
    setFilters,
    globalFilter: snap.globalFilter,
    setGlobalFilter,
    sorting: snap.sorting as SortingState,
    setSorting: handleSortingChange,
    pagination: snap.pagination as PaginationState,
    setPagination: handlePaginationChange,
    currentPage,
    setCurrentPage,
    setPageSize,
    defaultPageSize,
    defaultSorting: initialSorting as SortingState,
    defaultFilters: initialFilters,
    resetFilters,
    resetToDefaults,
  };
}
